import { ColDef, SuppressKeyboardEventParams } from 'ag-grid-community'
import { useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { ProgressReportTableContent } from '../components/table/ProgressReportTable'
import { FontWeight } from '../../../../styles/commonStyles'
import { colorPalette } from '../../../style/colorPalette'
import { ProgressReportTeamCellRenderer } from '../components/cell/ProgressReportTeamCellRenderer'
import { ReportStatusCellRenderer } from '../components/cell/ReportStatusCellRenderer'
import { ReportStatusCellEditor } from '../components/cell/ReportStatusCellEditor'
import { handleStatusCellKeyboardEvent } from '../components/cell/handleStatusCellKeyboardEvent'
import { CommentCellRenderer } from '../components/cell/CommentCellRenderer'
import { ProgressRateDiffCellRenderer } from '../components/cell/ProgressRateDiffCellRenderer'
import { VersionReportStatus } from '../../../../domain/entity/version-report/VersionReportVersionEntity'
import { ProcessRowNumberCellRenderer } from '../components/cell/ProcessRowNumberCellRenderer'
import { NumericDrillDownCellRenderer } from '../components/cell/NumericDrillDownCellRenderer'
import { ProgressReportNameCellRenderer } from '../components/cell/ProgressReportNameCellRenderer'
import { WbsItemStatusChipCellRenderer } from '../components/cell/WbsItemStatusChipCellRenderer'
import { WbsItemAggregateCategory } from '../../../../domain/value-object/ProjectPlanSnapshot'
import DateVO from '../../../../vo/DateVO'
import { DISPLAY_DATE_FORMAT_WITH_DAY } from '../../../../utils/date'

const numericCellStyle = {
  fontWeight: FontWeight.BOLD,
  justifyContent: 'flex-end',
  fontSize: '13px',
}

const formatScheduledDate = (value?: string): string =>
  value ? (DateVO.parse(value)?.format(DISPLAY_DATE_FORMAT_WITH_DAY) ?? '') : ''

const isMaxChildScheduledEndDateLate = (
  data?: ProgressReportTableContent
): boolean => {
  if (!data?.scheduledEndDate || !data.maxChildScheduledEndDate) return false
  const scheduledEndDate = DateVO.parse(data.scheduledEndDate)
  const maxChildScheduledEndDate = DateVO.parse(data.maxChildScheduledEndDate)
  if (!scheduledEndDate || !maxChildScheduledEndDate) return false
  return maxChildScheduledEndDate.isAfter(scheduledEndDate)
}

type NumericCellClickHandler = (
  data: ProgressReportTableContent,
  anchorEl: HTMLElement,
  clickPosition: { x: number; y: number },
  aggregateType: WbsItemAggregateCategory
) => void

type ColumnDefProps = {
  editable: boolean
  updateTableContent: (
    content: { uuid: string; status: VersionReportStatus; comment: string },
    rowIndex: number
  ) => void
  onNumericCellClick?: NumericCellClickHandler
}

export const useProcessReportColumnDef = ({
  editable,
  updateTableContent,
  onNumericCellClick,
}: ColumnDefProps) => {
  const intl = useIntl()
  const updateTableContentRef = useRef(updateTableContent)
  useEffect(() => {
    // for avoiding re-rendering by updateTableContent change
    updateTableContentRef.current = updateTableContent
  }, [updateTableContent])

  const columns = useMemo<ColDef<ProgressReportTableContent>[]>(
    () => [
      {
        colId: 'drag',
        headerName: '',
        width: 30,
        maxWidth: 30,
        resizable: false,
        pinned: 'left',
        lockPosition: 'left',
        lockPinned: true,
        filter: false,
        editable: false,
        suppressMovable: true,
        suppressColumnsToolPanel: true,
        suppressNavigable: true,
        hide: !editable,
        rowDrag: editable,
        rowDragText: () => '',
        cellClassRules: {
          'hover-over-can-drop': params =>
            !!params.context?.draggableNodeId &&
            params.node?.id === params.context.draggableNodeId,
        },
      },
      {
        headerName: '#',
        colId: 'rowNumber',
        pinned: 'left',
        lockPosition: 'left',
        lockPinned: !editable,
        suppressMovable: true,
        width: 29,
        filter: false,
        editable: false,
        valueGetter: params => (params.node?.rowIndex ?? 0) + 1,
        cellRenderer: ProcessRowNumberCellRenderer,
        cellClass: 'ag-numeric-cell',
        cellStyle: {
          fontWeight: FontWeight.NORMAL,
          justifyContent: 'center',
        },
      },
      {
        headerName: '名称',
        field: 'name',
        pinned: 'left',
        lockPinned: !editable,
        wrapText: true,
        width: 300,
        cellStyle: {
          fontWeight: FontWeight.BOLD,
          justifyContent: 'flex-start',
        },
        cellRenderer: ProgressReportNameCellRenderer,
        cellRendererParams: {
          targetType: 'PROCESS',
          editable,
        },
      },
      {
        headerName: 'チーム',
        field: 'team',
        hide: true,
        pinned: 'left',
        lockPinned: !editable,
        width: 110,
        cellStyle: {
          fontWeight: FontWeight.NORMAL,
          justifyContent: 'center',
        },
        cellRenderer: ProgressReportTeamCellRenderer,
      },
      {
        headerName: 'ステータス',
        field: 'status',
        pinned: 'left',
        lockPinned: !editable,
        width: 90,
        cellStyle: {
          fontWeight: FontWeight.NORMAL,
          justifyContent: 'center',
        },
        cellRenderer: ReportStatusCellRenderer,
        editable,
        cellEditor: ReportStatusCellEditor,
        cellEditorPopup: true,
        cellEditorPopupPosition: 'under',
        suppressKeyboardEvent: handleStatusCellKeyboardEvent,
        onCellValueChanged: params => {
          const rowIndex = params.node?.rowIndex
          if (rowIndex == null) return
          // custom editor のため確実な新値である newValue を読む
          const content = {
            uuid: params.data.uuid,
            status: params.newValue as VersionReportStatus,
            comment: params.data.comment,
          }
          updateTableContentRef.current(content, rowIndex)
        },
      },
      {
        headerName: '進捗率\n予実乖離',
        headerClass: 'multiline-header',
        wrapHeaderText: true,
        autoHeaderHeight: true,
        field: 'progressRateDiff',
        pinned: 'left',
        lockPinned: !editable,
        width: 75,
        cellClass: 'ag-numeric-cell',
        cellRenderer: ProgressRateDiffCellRenderer,
      },
      {
        headerName: '工程\nステータス',
        headerClass: 'multiline-header',
        wrapHeaderText: true,
        autoHeaderHeight: true,
        field: 'wbsItemStatus',
        pinned: 'left',
        lockPinned: !editable,
        width: 90,
        cellRenderer: WbsItemStatusChipCellRenderer,
      },
      {
        headerName: '予定開始日',
        field: 'scheduledStartDate',
        pinned: 'left',
        lockPinned: !editable,
        width: 90,
        valueFormatter: params => formatScheduledDate(params.value),
        cellStyle: {
          fontWeight: FontWeight.NORMAL,
          justifyContent: 'flex-end',
        },
      },
      {
        headerName: '予定終了日',
        field: 'scheduledEndDate',
        pinned: 'left',
        lockPinned: !editable,
        width: 90,
        valueFormatter: params => formatScheduledDate(params.value),
        cellStyle: {
          fontWeight: FontWeight.NORMAL,
          justifyContent: 'flex-end',
        },
      },
      {
        headerName: '子の最大\n予定終了日',
        headerClass: 'multiline-header',
        wrapHeaderText: true,
        autoHeaderHeight: true,
        field: 'maxChildScheduledEndDate',
        pinned: 'left',
        lockPinned: !editable,
        width: 90,
        valueFormatter: params => formatScheduledDate(params.value),
        cellStyle: params => ({
          fontWeight: FontWeight.NORMAL,
          justifyContent: 'flex-end',
          ...(isMaxChildScheduledEndDateLate(params.data)
            ? { color: colorPalette.pink[5] }
            : {}),
        }),
        tooltipValueGetter: params =>
          isMaxChildScheduledEndDateLate(params.data)
            ? intl.formatMessage({
                id: 'versionReport.progressReport.maxChildEndDateLate.tooltip',
              })
            : '',
      },
      {
        headerName: '予定進捗率',
        field: 'scheduledProgressRate',
        width: 90,
        cellClass: 'ag-numeric-cell',
        cellStyle: numericCellStyle,
        lockPinned: !editable,
      },
      {
        headerName: '進捗率',
        field: 'progressRate',
        width: 90,
        cellClass: 'ag-numeric-cell',
        cellStyle: numericCellStyle,
        lockPinned: !editable,
      },
      {
        headerName: '総数',
        field: 'total',
        width: 60,
        cellClass: 'ag-numeric-cell',
        cellStyle: numericCellStyle,
        lockPinned: !editable,
        cellRenderer: NumericDrillDownCellRenderer,
        cellRendererParams: {
          onNumericCellClick,
        },
      },
      {
        headerName: '予定',
        field: 'planned',
        width: 60,
        cellClass: 'ag-numeric-cell',
        cellStyle: numericCellStyle,
        lockPinned: !editable,
        cellRenderer: NumericDrillDownCellRenderer,
        cellRendererParams: {
          onNumericCellClick,
        },
      },
      {
        headerName: '完了',
        field: 'completed',
        width: 60,
        cellClass: 'ag-numeric-cell',
        cellStyle: numericCellStyle,
        lockPinned: !editable,
        cellRenderer: NumericDrillDownCellRenderer,
        cellRendererParams: {
          onNumericCellClick,
        },
      },
      {
        headerName: '遅延',
        field: 'delay',
        width: 60,
        cellClass: 'ag-numeric-cell',
        cellStyle: numericCellStyle,
        lockPinned: !editable,
        cellRenderer: NumericDrillDownCellRenderer,
        cellRendererParams: {
          onNumericCellClick,
        },
      },
      {
        headerName: '残',
        field: 'remaining',
        width: 60,
        cellClass: 'ag-numeric-cell',
        cellStyle: numericCellStyle,
        lockPinned: !editable,
        cellRenderer: NumericDrillDownCellRenderer,
        cellRendererParams: {
          onNumericCellClick,
        },
      },
      {
        headerName: '先行',
        field: 'preceding',
        width: 60,
        cellClass: 'ag-numeric-cell',
        cellStyle: numericCellStyle,
        lockPinned: !editable,
        cellRenderer: NumericDrillDownCellRenderer,
        cellRendererParams: {
          onNumericCellClick,
        },
      },
      {
        headerName: '未計画',
        field: 'unplanned',
        width: 60,
        cellClass: 'ag-numeric-cell',
        cellStyle: numericCellStyle,
        lockPinned: !editable,
        cellRenderer: NumericDrillDownCellRenderer,
        cellRendererParams: {
          onNumericCellClick,
        },
      },
      {
        headerName: '定性コメント',
        field: 'comment',
        width: 600,
        editable,
        lockPinned: !editable,
        cellClass: 'comment-cell-expandable',
        cellRenderer: CommentCellRenderer,
        cellEditor: 'agLargeTextCellEditor',
        cellEditorPopup: true,
        cellEditorParams: {
          maxLength: 10000,
          rows: 15,
          cols: 70,
        },
        suppressKeyboardEvent: suppressEnterKeyWithoutShift,
        onCellValueChanged: params => {
          const rowIndex = params.node?.rowIndex
          if (rowIndex == null) return
          const content = {
            uuid: params.data.uuid,
            status: params.data.status,
            comment: params.newValue as string,
          }
          updateTableContentRef.current(content, rowIndex)
        },
      },
    ],
    [editable, onNumericCellClick, intl]
  )
  return columns
}

export const useTeamReportColumnDef = ({
  editable,
  updateTableContent,
  onNumericCellClick,
}: ColumnDefProps) => {
  const updateTableContentRef = useRef(updateTableContent)
  useEffect(() => {
    // for avoiding re-rendering by updateTableContent change
    updateTableContentRef.current = updateTableContent
  }, [updateTableContent])

  const columns = useMemo<ColDef<ProgressReportTableContent>[]>(
    () => [
      {
        colId: 'drag',
        headerName: '',
        width: 30,
        maxWidth: 30,
        resizable: false,
        pinned: 'left',
        lockPosition: 'left',
        lockPinned: true,
        filter: false,
        editable: false,
        suppressMovable: true,
        suppressColumnsToolPanel: true,
        suppressNavigable: true,
        hide: !editable,
        rowDrag: editable,
        rowDragText: () => '',
        cellClassRules: {
          'hover-over-can-drop': params =>
            !!params.context?.draggableNodeId &&
            params.node?.id === params.context.draggableNodeId,
        },
      },
      {
        headerName: '#',
        colId: 'rowNumber',
        pinned: 'left',
        lockPosition: 'left',
        lockPinned: !editable,
        suppressMovable: true,
        width: 40,
        filter: false,
        editable: false,
        valueGetter: params => (params.node?.rowIndex ?? 0) + 1,
        cellClass: 'ag-numeric-cell',
        cellStyle: {
          fontWeight: FontWeight.NORMAL,
          justifyContent: 'center',
        },
      },
      {
        headerName: '名称',
        field: 'name',
        pinned: 'left',
        lockPinned: !editable,
        width: 300,
        cellStyle: {
          fontWeight: FontWeight.BOLD,
          justifyContent: 'flex-start',
        },
        cellRenderer: ProgressReportNameCellRenderer,
        cellRendererParams: {
          targetType: 'TEAM',
          editable,
        },
      },
      {
        headerName: 'ステータス',
        field: 'status',
        pinned: 'left',
        lockPinned: !editable,
        width: 90,
        cellStyle: {
          fontWeight: FontWeight.NORMAL,
          justifyContent: 'center',
        },
        cellRenderer: ReportStatusCellRenderer,
        editable,
        cellEditor: ReportStatusCellEditor,
        cellEditorPopup: true,
        cellEditorPopupPosition: 'under',
        suppressKeyboardEvent: handleStatusCellKeyboardEvent,
        onCellValueChanged: params => {
          const rowIndex = params.node?.rowIndex
          if (rowIndex == null) return
          const content = {
            uuid: params.data.uuid,
            status: params.newValue as VersionReportStatus,
            comment: params.data.comment,
          }
          updateTableContentRef.current(content, rowIndex)
        },
      },
      {
        headerName: '進捗率\n予実乖離',
        headerClass: 'multiline-header',
        wrapHeaderText: true,
        autoHeaderHeight: true,
        field: 'progressRateDiff',
        pinned: 'left',
        lockPinned: !editable,
        width: 75,
        cellClass: 'ag-numeric-cell',
        cellRenderer: ProgressRateDiffCellRenderer,
      },
      {
        headerName: '予定進捗率',
        field: 'scheduledProgressRate',
        width: 90,
        cellClass: 'ag-numeric-cell',
        cellStyle: numericCellStyle,
        lockPinned: !editable,
      },
      {
        headerName: '進捗率',
        field: 'progressRate',
        width: 90,
        cellClass: 'ag-numeric-cell',
        cellStyle: numericCellStyle,
        lockPinned: !editable,
      },
      {
        headerName: '総数',
        field: 'total',
        width: 60,
        cellClass: 'ag-numeric-cell',
        cellStyle: numericCellStyle,
        lockPinned: !editable,
        cellRenderer: NumericDrillDownCellRenderer,
        cellRendererParams: {
          onNumericCellClick,
        },
      },
      {
        headerName: '予定',
        field: 'planned',
        width: 60,
        cellClass: 'ag-numeric-cell',
        cellStyle: numericCellStyle,
        lockPinned: !editable,
        cellRenderer: NumericDrillDownCellRenderer,
        cellRendererParams: {
          onNumericCellClick,
        },
      },
      {
        headerName: '完了',
        field: 'completed',
        width: 60,
        cellClass: 'ag-numeric-cell',
        cellStyle: numericCellStyle,
        lockPinned: !editable,
        cellRenderer: NumericDrillDownCellRenderer,
        cellRendererParams: {
          onNumericCellClick,
        },
      },
      {
        headerName: '遅延',
        field: 'delay',
        width: 60,
        cellClass: 'ag-numeric-cell',
        cellStyle: numericCellStyle,
        lockPinned: !editable,
        cellRenderer: NumericDrillDownCellRenderer,
        cellRendererParams: {
          onNumericCellClick,
        },
      },
      {
        headerName: '残',
        field: 'remaining',
        width: 60,
        cellClass: 'ag-numeric-cell',
        cellStyle: numericCellStyle,
        lockPinned: !editable,
        cellRenderer: NumericDrillDownCellRenderer,
        cellRendererParams: {
          onNumericCellClick,
        },
      },
      {
        headerName: '先行',
        field: 'preceding',
        width: 60,
        cellClass: 'ag-numeric-cell',
        cellStyle: numericCellStyle,
        lockPinned: !editable,
        cellRenderer: NumericDrillDownCellRenderer,
        cellRendererParams: {
          onNumericCellClick,
        },
      },
      {
        headerName: '未計画',
        field: 'unplanned',
        width: 60,
        cellClass: 'ag-numeric-cell',
        cellStyle: numericCellStyle,
        lockPinned: !editable,
        cellRenderer: NumericDrillDownCellRenderer,
        cellRendererParams: {
          onNumericCellClick,
        },
      },
      {
        headerName: '定性コメント',
        field: 'comment',
        width: 600,
        editable,
        lockPinned: !editable,
        cellClass: 'comment-cell-expandable',
        cellRenderer: CommentCellRenderer,
        cellEditor: 'agLargeTextCellEditor',
        cellEditorPopup: true,
        cellEditorParams: {
          maxLength: 10000,
          rows: 15,
          cols: 70,
        },
        suppressKeyboardEvent: suppressEnterKeyWithoutShift,
        onCellValueChanged: params => {
          const rowIndex = params.node?.rowIndex
          if (rowIndex == null) return
          const content = {
            uuid: params.data.uuid,
            status: params.data.status,
            comment: params.newValue as string,
          }
          updateTableContentRef.current(content, rowIndex)
        },
      },
    ],
    [editable, onNumericCellClick]
  )
  return columns
}

const suppressEnterKeyWithoutShift = (
  params: SuppressKeyboardEventParams<ProgressReportTableContent>
) => {
  return params.editing && params.event.key === 'Enter' && params.event.shiftKey
}
